/**
 * Build-time seller org configuration (VALAR / TAZTY / MSME).
 * Layout, auth copy, and feature flags are static per build.
 * Logo, favicon, and WhatsApp come from GET /organization/{code} at runtime.
 */

import ondcBadge from "../Assets/ondc.png";
import taztyBadge from "../Assets/Logos/tazty_partner.png";
import { updateDocumentSocialMeta } from "../utils/socialMeta";

export type SellerOrgCode = "valar" | "tazty" | "msme";
export type SellerChannel = "ondc" | "direct" | "msme";
export type AuthLayoutKind = "valar" | "partner";
export type LoginMethod = "password" | "otp";
export type MarketingNetworkBadge = "ondc" | "tazty";

/** MSME auth left-panel feature list presentation variant. */
export type MsmeAuthMarketingVariant = "colored-cards" | "ondc-grid";

export const DEFAULT_MSME_AUTH_MARKETING_VARIANT: MsmeAuthMarketingVariant =
  "ondc-grid";

export interface AuthMarketingCard {
  title: string;
  description: string;
}

export interface AuthFooterLink {
  label: string;
  href?: string;
}

export interface SellerOrgBranding {
  documentTitle: string;
  /** Remote favicon from organization API. */
  faviconUrl?: string;
  appleTouchIconUrl?: string;
  authHeaderLogo: string;
  authHeaderLogoAlt: string;
  sidebarLogo: string;
  sidebarLogoAlt: string;
  sidebarLogoHeight: string;
  sidebarLogoMinWidth: string;
  whatsappHref: string;
  whatsappDisplay: string;
  showOndcFooter: boolean;
  /** When set, renders a custom auth footer line (MSME). */
  authFooterText?: string;
  authFooterLayout?: "center" | "left" | "split";
  authFooterLinks?: AuthFooterLink[];
  termsUrl?: string;
  privacyUrl?: string;
  marketingNetworkBadge: MarketingNetworkBadge;
  marketingHeadlineSuffix: string;
  marketingSubheadline: string;
  primaryAccentColor?: string;
}

/** Organization API fields used to override static branding. */
export interface OrganizationApiBranding {
  organization_name?: string;
  logo?: string;
  favicon_url?: string;
  whatsapp_support_number?: string;
  short_description?: string;
}

export interface SellerOrgAuthCopy {
  loginTitle: string;
  loginSubtitle: string;
  registerLinkLabel: string;
  registrationTitle: string;
  registrationSubtitle: string;
  partnerRegistrationTitle: string;
  partnerRegistrationSubtitle: string;
  /** Shared email field hint for login and registration. */
  emailPlaceholder?: string;
}

export interface SellerOrgFeatures {
  buyerConfigMenu: boolean;
  serviceableAreaMenu: boolean;
  analyticsMenu: boolean;
  masterMenu: boolean;
  fnbOnlyRegistration: boolean;
  showInventoryToggle: boolean;
  storeProductsMenuLabel: "Products" | "Menu";
  entityNameSingular: string;
  entityNamePlural: string;
  fcmEnabled: boolean;
  defaultStoreCity: string;
  defaultStoreState: string;
}

export interface SellerOrgBuildConfig {
  code: SellerOrgCode;
  channel: SellerChannel;
  auth: {
    layout: AuthLayoutKind;
    loginMethod: LoginMethod;
    copy: SellerOrgAuthCopy;
  };
  branding: SellerOrgBranding;
  features: SellerOrgFeatures;
  authMarketing: {
    loginCards: AuthMarketingCard[];
    registrationCards: AuthMarketingCard[];
    taztyCards: AuthMarketingCard[];
    /** MSME-only: left auth panel design variant. */
    msmePanelVariant?: MsmeAuthMarketingVariant;
  };
}

function cleanEnvValue(value: string | undefined): string {
  return value?.replace(/"/g, "").trim() || "";
}

/** Browser tab title: REACT_APP_TITLE wins over organization API name. */
export function resolveDocumentTitle(orgName?: string): string {
  const envTitle = cleanEnvValue(process.env.REACT_APP_TITLE);
  if (envTitle) {
    return envTitle;
  }
  const trimmedOrg = orgName?.trim();
  if (trimmedOrg) {
    return trimmedOrg;
  }
  return "Seller Panel";
}

/** Shell branding — logo / favicon URL / WhatsApp filled by organization API. */
function createBrandingShell(overrides: Partial<SellerOrgBranding>): SellerOrgBranding {
  return {
    documentTitle: resolveDocumentTitle(),
    authHeaderLogo: "",
    authHeaderLogoAlt: "Organization",
    sidebarLogo: "",
    sidebarLogoAlt: "Organization",
    sidebarLogoHeight: "50px",
    sidebarLogoMinWidth: "60px",
    whatsappHref: "",
    whatsappDisplay: "",
    showOndcFooter: false,
    marketingNetworkBadge: "ondc",
    marketingHeadlineSuffix: "",
    marketingSubheadline: "",
    ...overrides,
  };
}

const ONDC_REGISTRATION_CARDS: AuthMarketingCard[] = [
  {
    title: "50 Million+ Buyers",
    description: "Access a vast customer base across India through ONDC.",
  },
  {
    title: "1000+ Cities & Towns",
    description: "Deliver orders to customers wherever they are.",
  },
  {
    title: "300+ Product Categories",
    description: "Sell everything from groceries to fashion with ease.",
  },
  {
    title: "Multiple Delivery Partners",
    description: "Seamless integration with leading delivery networks.",
  },
];

const ONDC_LOGIN_CARDS: AuthMarketingCard[] = [
  {
    title: "Seamless Product Sync",
    description: "Sync your catalog with ONDC quickly and hassle-free.",
  },
  {
    title: "Maximize Reach Across ONDC Buyer Apps",
    description: "Get your store and products visible on multiple ONDC buyer apps.",
  },
  {
    title: "300+ Product Categories",
    description: "Sell everything from groceries to fashion with ease.",
  },
  {
    title: "Multiple Delivery Partners",
    description: "Seamless integration with leading delivery networks.",
  },
];

const TAZTY_MARKETING_CARDS: AuthMarketingCard[] = [
  {
    title: "Partner with Tazty",
    description: "Join Tazty and get your cloud kitchen discovered by food lovers across cities.",
  },
  {
    title: "Expand Your F&B Reach",
    description: "Grow your cloud kitchen's presence on multiple delivery and discovery platforms.",
  },
  {
    title: "Simple Menu & Order Management",
    description:
      "Easily manage your cloud kitchen's menu, pricing, availability, and receive orders efficiently.",
  },
  {
    title: "Fast & Reliable Delivery",
    description:
      "Partnered with top delivery networks to ensure your cloud kitchen's food reaches customers hot and fresh.",
  },
];

const MSME_TERMS_URL = "/terms";
const MSME_PRIVACY_URL = "/privacy";

const VALAR_TERMS_URL =
  "https://sqc-bucket.in-maa-1.linodeobjects.com/Policies%2FValarDigital%20Commerce%20-%20Seller%20Terms%20%26%20Conditions.pdf";
const VALAR_PRIVACY_URL =
  "https://sqc-bucket.in-maa-1.linodeobjects.com/Policies%2FValar%20Privacy%20Policy.pdf";

const valarBaseFeatures: SellerOrgFeatures = {
  buyerConfigMenu: false,
  serviceableAreaMenu: false,
  analyticsMenu: true,
  masterMenu: true,
  fnbOnlyRegistration: false,
  showInventoryToggle: true,
  storeProductsMenuLabel: "Products",
  entityNameSingular: "Store",
  entityNamePlural: "Stores",
  fcmEnabled: true,
  defaultStoreCity: "",
  defaultStoreState: "",
};

const taztyFeatures: SellerOrgFeatures = {
  buyerConfigMenu: true,
  serviceableAreaMenu: true,
  analyticsMenu: false,
  masterMenu: false,
  fnbOnlyRegistration: true,
  showInventoryToggle: false,
  storeProductsMenuLabel: "Menu",
  entityNameSingular: "Restaurant",
  entityNamePlural: "Restaurants",
  fcmEnabled: true,
  defaultStoreCity: "MADURAI",
  defaultStoreState: "TAMIL NADU",
};

/** MSME: mirrors Valar until product confirms MSME-specific behavior (see open questions). */
const msmeFeatures: SellerOrgFeatures = {
  ...valarBaseFeatures,
  fcmEnabled: false,
};

export const SELLER_ORG_BUILD_CONFIG: Record<SellerOrgCode, SellerOrgBuildConfig> = {
  valar: {
    code: "valar",
    channel: "ondc",
    auth: {
      layout: "valar",
      loginMethod: "password",
      copy: {
        loginTitle: "Seller Login",
        loginSubtitle: "Access your seller dashboard to manage your store and orders.",
        registerLinkLabel: "Register Now",
        registrationTitle: "Register to Sell on ONDC",
        registrationSubtitle:
          "Join the ONDC network and grow your business online. Complete your registration in a few easy steps.",
        partnerRegistrationTitle: "",
        partnerRegistrationSubtitle: "",
      },
    },
    branding: createBrandingShell({
      showOndcFooter: true,
      termsUrl: VALAR_TERMS_URL,
      privacyUrl: VALAR_PRIVACY_URL,
      marketingNetworkBadge: "ondc",
      marketingHeadlineSuffix: "ONDC",
      marketingSubheadline:
        "Join the ONDC network and grow your business online. Complete your registration in a few easy steps.",
    }),
    features: valarBaseFeatures,
    authMarketing: {
      loginCards: ONDC_LOGIN_CARDS,
      registrationCards: ONDC_REGISTRATION_CARDS,
      taztyCards: TAZTY_MARKETING_CARDS,
    },
  },
  tazty: {
    code: "tazty",
    channel: "direct",
    auth: {
      layout: "partner",
      loginMethod: "otp",
      copy: {
        loginTitle: "Partner Login",
        loginSubtitle: "Access your dashboard to manage your restaurant and orders.",
        registerLinkLabel: "Apply to Partner",
        registrationTitle: "Become a Tazty Partner",
        registrationSubtitle:
          "Join us and grow your food business online. Complete your registration in a few easy steps.",
        partnerRegistrationTitle: "Become a Tazty Partner",
        partnerRegistrationSubtitle:
          "Join us and grow your food business online. Complete your registration in a few easy steps.",
      },
    },
    branding: createBrandingShell({
      sidebarLogoHeight: "35px",
      sidebarLogoMinWidth: "35px",
      marketingNetworkBadge: "tazty",
      marketingHeadlineSuffix: "Tazty",
      marketingSubheadline:
        "Join us and grow your food business online. Complete your registration in a few easy steps.",
      primaryAccentColor: "#006e2f",
    }),
    features: taztyFeatures,
    authMarketing: {
      loginCards: TAZTY_MARKETING_CARDS,
      registrationCards: TAZTY_MARKETING_CARDS,
      taztyCards: TAZTY_MARKETING_CARDS,
    },
  },
  msme: {
    code: "msme",
    channel: "msme",
    auth: {
      layout: "valar",
      loginMethod: "password",
      copy: {
        loginTitle: "MSME B2C - Seller Login",
        loginSubtitle:
          "Empowering MSMEs to sell, manage, and grow their business through the ONDC digital commerce ecosystem.",
        registerLinkLabel: "Register Now",
        registrationTitle: "Register to Sell on ONDC Network",
        registrationSubtitle:
          "Empowering MSMEs to sell, manage, and grow their business through the ONDC digital commerce ecosystem.",
        partnerRegistrationTitle: "",
        partnerRegistrationSubtitle: "",
        emailPlaceholder: "eg: your@business.com",
      },
    },
    branding: createBrandingShell({
      showOndcFooter: false,
      authFooterLayout: "split",
      authFooterText:
        "MSME Global Mart · Powered by NSIC, Government of India Enterprise. All rights reserved.",
      authFooterLinks: [
        { label: "Privacy", href: MSME_PRIVACY_URL },
        { label: "Terms", href: MSME_TERMS_URL },
      ],
      termsUrl: MSME_TERMS_URL,
      privacyUrl: MSME_PRIVACY_URL,
      marketingNetworkBadge: "ondc",
      marketingHeadlineSuffix: "MSME",
      marketingSubheadline:
        "Join the ONDC network and grow your business online. Complete your registration in a few easy steps.",
    }),
    features: msmeFeatures,
    authMarketing: {
      loginCards: ONDC_LOGIN_CARDS,
      registrationCards: ONDC_REGISTRATION_CARDS,
      taztyCards: TAZTY_MARKETING_CARDS,
      msmePanelVariant: "ondc-grid", // colored-cards
    },
  },
};

let organizationBrandingOverride: Partial<SellerOrgBranding> | null = null;
let organizationBrandingLoaded = false;
const brandingListeners = new Set<() => void>();

export function isOrganizationBrandingLoaded(): boolean {
  return organizationBrandingLoaded;
}

function setOrganizationBrandingLoaded(loaded: boolean): void {
  organizationBrandingLoaded = loaded;
  notifyBrandingListeners();
}

function notifyBrandingListeners(): void {
  brandingListeners.forEach((listener) => listener());
}

/** Normalize WhatsApp number from API to wa.me link + display label. */
export function formatWhatsappFromApi(raw: string): { href: string; display: string } {
  const trimmed = raw.trim();
  const display = trimmed.startsWith("+") ? trimmed : `+${trimmed.replace(/\D/g, "")}`;
  const digits = trimmed.replace(/\D/g, "");
  return {
    href: `https://wa.me/${digits}`,
    display: display || trimmed,
  };
}

/** Map organization API branding onto seller org config (logo, favicon, WhatsApp). */
export function applyOrganizationBrandingFromApi(
  details: OrganizationApiBranding,
): void {
  const override: Partial<SellerOrgBranding> = {};
  const orgName = details.organization_name?.trim();

  if (details.logo?.trim()) {
    const logoUrl = details.logo.trim();
    override.authHeaderLogo = logoUrl;
    override.sidebarLogo = logoUrl;
    if (orgName) {
      override.authHeaderLogoAlt = orgName;
      override.sidebarLogoAlt = orgName;
    }
  }

  if (details.favicon_url?.trim()) {
    const faviconUrl = details.favicon_url.trim();
    override.faviconUrl = faviconUrl;
    override.appleTouchIconUrl = faviconUrl;
  }

  if (details.whatsapp_support_number?.trim()) {
    const whatsapp = formatWhatsappFromApi(details.whatsapp_support_number);
    override.whatsappHref = whatsapp.href;
    override.whatsappDisplay = whatsapp.display;
  }

  override.documentTitle = resolveDocumentTitle(orgName);

  organizationBrandingOverride = {
    ...organizationBrandingOverride,
    ...override,
  };

  updateDocumentSocialMeta({
    title: override.documentTitle,
    description: details.short_description?.trim(),
    imageUrl: details.logo?.trim() || details.favicon_url?.trim(),
    siteUrl: process.env.REACT_APP_SITE_URL?.replace(/"/g, "").trim(),
  });

  setOrganizationBrandingLoaded(true);
  notifyBrandingListeners();
}

/** Mark branding fetch failed — UI keeps empty logo/WhatsApp until retry. */
export function clearOrganizationBrandingLoadState(): void {
  setOrganizationBrandingLoaded(false);
}

export function subscribeSellerOrgBranding(listener: () => void): () => void {
  brandingListeners.add(listener);
  return () => brandingListeners.delete(listener);
}

function mergeBranding(base: SellerOrgBranding): SellerOrgBranding {
  if (!organizationBrandingOverride) {
    return base;
  }
  return { ...base, ...organizationBrandingOverride };
}

/** Favicon / notification icon from organization API only (no bundled asset paths). */
export function resolveNotificationIconUrl(branding: SellerOrgBranding): string {
  return (
    branding.faviconUrl?.trim() ||
    branding.appleTouchIconUrl?.trim() ||
    branding.authHeaderLogo?.trim() ||
    ""
  );
}

export function resolveFaviconHref(branding: SellerOrgBranding): string {
  return resolveNotificationIconUrl(branding);
}

export function resolveAppleTouchIconHref(branding: SellerOrgBranding): string {
  return (
    branding.appleTouchIconUrl?.trim() ||
    branding.faviconUrl?.trim() ||
    branding.authHeaderLogo?.trim() ||
    ""
  );
}

export function getSellerOrgCodeNormalized(): string {
  return (
    process.env.REACT_APP_ORG_CODE?.replace(/"/g, "").trim().toLowerCase() ||
    ""
  );
}

export function getBaseSellerOrgBuildConfig(): SellerOrgBuildConfig {
  const code = getSellerOrgCodeNormalized() as SellerOrgCode;
  const config = SELLER_ORG_BUILD_CONFIG[code];
  if (!config) {
    throw new Error(
      `Unknown REACT_APP_ORG_CODE "${getSellerOrgCodeNormalized()}". Expected one of: ${Object.keys(SELLER_ORG_BUILD_CONFIG).join(", ")}.`,
    );
  }
  return config;
}

export function getSellerOrgBuildConfig(): SellerOrgBuildConfig {
  const config = getBaseSellerOrgBuildConfig();
  return {
    ...config,
    branding: mergeBranding(config.branding),
  };
}

export function getSellerOrgApiCode(): string {
  return getSellerOrgBuildConfig().code;
}

export function isPartnerAuthLayout(config: SellerOrgBuildConfig = getSellerOrgBuildConfig()): boolean {
  return config.auth.layout === "partner";
}

export function isPasswordLogin(config: SellerOrgBuildConfig = getSellerOrgBuildConfig()): boolean {
  return config.auth.loginMethod === "password";
}

export function getMarketingBadgeAsset(badge: MarketingNetworkBadge): string {
  return badge === "tazty" ? taztyBadge : ondcBadge;
}

/**
 * Channel badge for auth marketing headline ("Selling on ONDC" / Tazty).
 * Always the static network badge — never the organization API logo.
 */
export function resolveMarketingBadgeAsset(branding: SellerOrgBranding): string {
  return getMarketingBadgeAsset(branding.marketingNetworkBadge);
}

export function getAuthMarketingCards(
  config: SellerOrgBuildConfig,
  isRegistrationRoute: boolean,
): AuthMarketingCard[] {
  if (config.channel === "direct") {
    return config.authMarketing.taztyCards;
  }
  return isRegistrationRoute
    ? config.authMarketing.registrationCards
    : config.authMarketing.loginCards;
}

export function getMsmeAuthMarketingVariant(
  config: SellerOrgBuildConfig = getSellerOrgBuildConfig(),
): MsmeAuthMarketingVariant {
  return config.authMarketing.msmePanelVariant ?? DEFAULT_MSME_AUTH_MARKETING_VARIANT;
}
